import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Profile } from '@/types/supabase';
import { toast } from '@/components/ui/use-toast';

export interface SharedUser extends Profile {
  share_id: string;
  access_level: 'view' | 'edit';
}

export const useBlueprintSharing = (blueprintId: string) => {
  const { user } = useAuth();
  const [connections, setConnections] = useState<Profile[]>([]);
  const [sharedWith, setSharedWith] = useState<SharedUser[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchConnections = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data: connectionsData, error: connectionsError } = await supabase
      .from('user_connections')
      .select('user_id, connected_user_id')
      .or(`user_id.eq.${user.id},connected_user_id.eq.${user.id}`)
      .eq('status', 'accepted');

    if (connectionsError) {
      console.error('Error fetching connections:', connectionsError);
      setLoading(false);
      return;
    }

    const connectedUserIds = connectionsData
      .map(c => (c.user_id === user.id ? c.connected_user_id : c.user_id))
      .filter(id => id !== user.id);

    if (connectedUserIds.length === 0) {
      setConnections([]);
      setLoading(false);
      return;
    }

    const { data: profilesData, error: profilesError } = await supabase
      .from('profiles')
      .select('*')
      .in('id', connectedUserIds);

    if (profilesError) {
      console.error('Error fetching profiles:', profilesError);
    } else {
      setConnections(profilesData || []);
    }
    setLoading(false);
  }, [user]);

  const fetchSharedWith = useCallback(async () => {
    if (!blueprintId) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('blueprint_shares')
      .select(`
        id,
        access_level,
        profiles:user_id (
          id,
          full_name,
          avatar_url,
          email
        )
      `)
      .eq('blueprint_id', blueprintId);

    if (error) {
      console.error('Error fetching shared users:', error);
      toast({ title: 'Error', description: 'Could not fetch sharing information.', variant: 'destructive' });
    } else {
      const formattedData = data
        .filter(item => item.profiles)
        .map(item => ({
          ...(item.profiles as Profile),
          share_id: item.id,
          access_level: item.access_level as 'view' | 'edit',
        }));
      setSharedWith(formattedData);
    }
    setLoading(false);
  }, [blueprintId]);

  const shareBlueprint = async (userId: string, accessLevel: 'view' | 'edit') => {
    if (!user || !blueprintId) return;
    
    if (sharedWith.some(su => su.id === userId)) {
        toast({ title: 'Already Shared', description: 'This blueprint is already shared with this user.', variant: 'default' });
        return;
    }

    const { error } = await supabase
      .from('blueprint_shares')
      .insert({
        blueprint_id: blueprintId,
        user_id: userId,
        shared_by: user.id,
        access_level: accessLevel,
      });

    if (error) {
      console.error('Error sharing blueprint:', error);
      toast({ title: 'Error', description: 'Failed to share blueprint.', variant: 'destructive' });
    } else {
      toast({ title: 'Success', description: 'Blueprint shared successfully.' });
      fetchSharedWith();
    }
  };

  const unshareBlueprint = async (shareId: string) => {
    const { error } = await supabase
      .from('blueprint_shares')
      .delete()
      .eq('id', shareId);

    if (error) {
      console.error('Error unsharing blueprint:', error);
      toast({ title: 'Error', description: 'Failed to remove share.', variant: 'destructive' });
    } else {
      toast({ title: 'Success', description: 'Blueprint sharing removed.' });
      fetchSharedWith();
    }
  };
  
  const updateShareAccess = async (shareId: string, accessLevel: 'view' | 'edit') => {
    const { error } = await supabase
      .from('blueprint_shares')
      .update({ access_level: accessLevel })
      .eq('id', shareId);

    if (error) {
      console.error('Error updating share access:', error);
      toast({ title: 'Error', description: 'Failed to update access level.', variant: 'destructive' });
    } else {
      toast({ title: 'Success', description: 'Access level updated.' });
      fetchSharedWith();
    }
  };

  useEffect(() => {
    fetchConnections();
    fetchSharedWith();
  }, [fetchConnections, fetchSharedWith]);

  return { connections, sharedWith, loading, shareBlueprint, unshareBlueprint, updateShareAccess };
};